import type { Product, Order, Customer, Coupon, Store, StoreStats } from '../types/index.js';
import { filterProductsBySearch } from '../search/product-search.js';

const DEMO_STORE_ID = 'store-1';

/** Stable R2 reference stored in D1; API resolves to absolute serve URL at response time. */
export function mockR2ImageRef(imageId: string, ext = 'png'): string {
  return `r2:stores/${DEMO_STORE_ID}/images/${imageId}.${ext}`;
}

/**
 * Mock product data based on The Golden Peptides design
 */
export const mockProducts: Product[] = [
  {
    id: 'bpc-157',
    name: 'BPC-157 (Acetate)',
    slug: 'bpc-157-acetate',
    description: 'Highest purity BPC-157 (Body Protection Compound-157) Acetate. Lyophilized powder for research purposes. Third-party tested for purity and identity.',
    shortDescription: 'High-purity BPC-157 for research. >99% purity by HPLC.',
    price: 63.00,
    compareAtPrice: 75.00,
    sku: 'BPC-157-5MG',
    trackInventory: true,
    inventoryQuantity: 150,
    weight: 0.1,
    status: 'active',
    images: [
      { id: 'img-1', url: mockR2ImageRef('img-1'), altText: 'BPC-157 5mg Lyophilized Powder Vial', position: 0 },
      { id: 'img-1b', url: mockR2ImageRef('img-1b'), altText: 'BPC-157 5mg vial alternate view', position: 1 },
    ],
    variants: [
      { id: 'bpc-5mg', productId: 'bpc-157', name: '5mg', sku: 'BPC-157-5MG', price: 63.00, inventoryQuantity: 150, options: { size: '5mg' }, inStock: true },
      { id: 'bpc-10mg', productId: 'bpc-157', name: '10mg', sku: 'BPC-157-10MG', price: 115.00, inventoryQuantity: 0, options: { size: '10mg' }, inStock: false },
    ],
    quantityDiscounts: [
      { id: 'qd-1', productId: 'bpc-157', minQuantity: 3, discountType: 'percentage', discountValue: 10 },
      { id: 'qd-2', productId: 'bpc-157', minQuantity: 5, discountType: 'percentage', discountValue: 15 },
    ],
    category: 'peptides',
    tags: ['research', 'laboratory', 'bpc157', 'angiogenesis', 'bestseller'],
    metadata: {
      purity: '99.4%',
      testMethod: 'HPLC',
      storageConditions: 'Store at -20°C. Protect from light. Keep in lyophilized form.',
      handlingInstructions: 'Handle with standard PPE. For laboratory research use only.',
      safetyInfo: 'Follow SDS guidelines. Irritation may occur with direct skin contact.',
      sequence: 'Gly-Glu-Pro-Pro-Pro-Gly-Lys-Pro-Ala-Asp-Asp-Ala-Gly-Leu-Val',
      format: 'Lyophilized White Powder',
      batchNumber: '4922',
      testDate: 'Oct 14, 2023',
      coaUrl: '#coa-bpc-157',
    },
    createdAt: '2023-01-15T10:00:00Z',
    updatedAt: '2024-01-20T14:30:00Z',
  },
  {
    id: 'tb-500',
    name: 'TB-500 (Thymosin Beta-4)',
    slug: 'tb-500-thymosin-beta-4',
    description: 'Premium TB-500 peptide (Thymosin Beta-4) for in-vitro research. Laboratory tested for purity and identity.',
    shortDescription: 'TB-500 peptide for laboratory research. >99% purity.',
    price: 55.00,
    sku: 'TB500-5MG',
    trackInventory: true,
    inventoryQuantity: 85,
    status: 'active',
    images: [
      { id: 'img-2', url: mockR2ImageRef('img-2'), altText: 'TB-500 5mg Lyophilized Powder Vial', position: 0 },
    ],
    variants: [
      { id: 'tb-5mg', productId: 'tb-500', name: '5mg', sku: 'TB500-5MG', price: 55.00, inventoryQuantity: 85, options: { size: '5mg' }, inStock: true },
    ],
    quantityDiscounts: [],
    category: 'peptides',
    tags: ['research', 'laboratory'],
    metadata: {
      purity: '99.1%',
      testMethod: 'HPLC',
      storageConditions: 'Store at -20°C. Protect from light. Keep in lyophilized form.',
      handlingInstructions: 'Handle with standard PPE. For laboratory research use only.',
      safetyInfo: 'Follow SDS guidelines. Handle with care in ventilated area.',
      sequence: 'Ac-Ser-Asp-Lys-Pro-Asp-Met-Ala-Glu-Ile-Glu-Lys-Phe-Asp-Lys-Ser-Lys-Leu-Lys-Lys-Thr-Glu-Thr-Gln-Glu-Lys-Asn-Pro-Leu-Pro-Ser-Lys-Glu-Thr-Ile-Glu-Gln-Glu-Lys-Gln-Ala-Gly-Glu-Ser',
      format: 'Lyophilized White Powder',
      batchNumber: '4925',
      testDate: 'Nov 02, 2023',
      coaUrl: '#coa-tb-500',
    },
    createdAt: '2023-02-10T10:00:00Z',
    updatedAt: '2024-01-15T11:00:00Z',
  },
  {
    id: 'ghk-cu',
    name: 'GHK-Cu (Copper Peptide)',
    slug: 'ghk-cu-copper-peptide',
    description: 'GHK-Cu copper peptide complex for laboratory research applications. Synthesized for high purity analytical testing.',
    shortDescription: 'Copper peptide complex for laboratory research.',
    price: 45.00,
    sku: 'GHKCU-50MG',
    trackInventory: true,
    inventoryQuantity: 120,
    status: 'active',
    images: [
      { id: 'img-3', url: mockR2ImageRef('img-3'), altText: 'GHK-Cu 50mg Copper Peptide Light Blue Powder Vial', position: 0 },
    ],
    variants: [
      { id: 'ghk-50mg', productId: 'ghk-cu', name: '50mg', sku: 'GHKCU-50MG', price: 45.00, inventoryQuantity: 120, options: { size: '50mg' }, inStock: true },
    ],
    quantityDiscounts: [],
    category: 'peptides',
    tags: ['research', 'laboratory'],
    metadata: {
      purity: '99.5%',
      testMethod: 'HPLC',
      storageConditions: 'Store at -20°C. Protect from light. Keep in lyophilized form.',
      handlingInstructions: 'Handle with standard PPE. For laboratory research use only.',
      safetyInfo: 'Follow SDS guidelines. Blue powder may stain surfaces.',
      sequence: 'Gly-His-Lys(Cu2+)',
      format: 'Lyophilized Blue Powder',
      batchNumber: '4930',
      testDate: 'Dec 15, 2023',
      coaUrl: '#coa-ghk-cu',
    },
    createdAt: '2023-03-01T10:00:00Z',
    updatedAt: '2024-01-10T09:00:00Z',
  },
  {
    id: 'ipamorelin',
    name: 'Ipamorelin 5mg',
    slug: 'ipamorelin-5mg',
    description: 'High-quality Ipamorelin peptide. Selective growth hormone secretagogue for in-vitro research purposes.',
    shortDescription: 'Selective GH secretagogue peptide for research.',
    price: 42.00,
    sku: 'IPA-5MG',
    trackInventory: true,
    inventoryQuantity: 200,
    status: 'active',
    images: [
      { id: 'img-4', url: mockR2ImageRef('img-4'), altText: 'Ipamorelin 5mg Lyophilized Powder Vial', position: 0 },
    ],
    variants: [
      { id: 'ipa-5mg', productId: 'ipamorelin', name: '5mg', sku: 'IPA-5MG', price: 42.00, inventoryQuantity: 200, options: { size: '5mg' }, inStock: true },
    ],
    quantityDiscounts: [],
    category: 'peptides',
    tags: ['research', 'laboratory'],
    metadata: {
      purity: '99.2%',
      testMethod: 'HPLC',
      storageConditions: 'Store at -20°C. Protect from light.',
      handlingInstructions: 'Handle with standard PPE. For laboratory research use only.',
      safetyInfo: 'Follow SDS guidelines.',
      sequence: 'Aib-His-D-2-Nal-D-Phe-Lys-NH2',
      format: 'Lyophilized White Powder',
      batchNumber: '4935',
      testDate: 'Jan 05, 2024',
      coaUrl: '#coa-ipamorelin',
    },
    createdAt: '2023-04-15T10:00:00Z',
    updatedAt: '2024-01-05T15:00:00Z',
  },
  {
    id: 'cjc-1295',
    name: 'CJC-1295 No DAC',
    slug: 'cjc-1295-no-dac',
    description: 'Modified GRF (1-29) peptide without Drug Affinity Complex. Research grade quality for laboratory use.',
    shortDescription: 'Modified GRF peptide for laboratory research.',
    price: 48.00,
    sku: 'CJC-2MG',
    trackInventory: true,
    inventoryQuantity: 95,
    status: 'active',
    images: [
      { id: 'img-5', url: mockR2ImageRef('img-5'), altText: 'CJC-1295 2mg Lyophilized Powder Vial', position: 0 },
    ],
    variants: [
      { id: 'cjc-2mg', productId: 'cjc-1295', name: '2mg', sku: 'CJC-2MG', price: 48.00, inventoryQuantity: 95, options: { size: '2mg' }, inStock: true },
    ],
    quantityDiscounts: [],
    category: 'peptides',
    tags: ['research', 'laboratory'],
    metadata: {
      purity: '99.0%',
      testMethod: 'HPLC',
      storageConditions: 'Store at -20°C. Protect from light.',
      handlingInstructions: 'Handle with standard PPE. For laboratory research use only.',
      safetyInfo: 'Follow SDS guidelines.',
      sequence: 'Tyr-D-Ala-Asp-Ala-Ile-Phe-Thr-Gln-Ser-Tyr-Arg-Lys-Val-Leu-Ala-Gln-Leu-Ser-Ala-Arg-Lys-Leu-Leu-Gln-Asp-Ile-Leu-Ser-Arg-NH2',
      format: 'Lyophilized White Powder',
      batchNumber: '4940',
      testDate: 'Jan 10, 2024',
      coaUrl: '#coa-cjc-1295',
    },
    createdAt: '2023-05-01T10:00:00Z',
    updatedAt: '2024-01-08T12:00:00Z',
  },
  {
    id: 'pt-141',
    name: 'PT-141 (Bremelanotide)',
    slug: 'pt-141-bremelanotide',
    description: 'PT-141 peptide, also known as Bremelanotide. Melanocortin receptor agonist for in-vitro research.',
    shortDescription: 'Melanocortin receptor agonist for laboratory research.',
    price: 52.00,
    sku: 'PT141-10MG',
    trackInventory: true,
    inventoryQuantity: 60,
    status: 'active',
    images: [
      { id: 'img-6', url: mockR2ImageRef('img-6'), altText: 'PT-141 10mg Lyophilized Powder Vial', position: 0 },
    ],
    variants: [
      { id: 'pt-10mg', productId: 'pt-141', name: '10mg', sku: 'PT141-10MG', price: 52.00, inventoryQuantity: 60, options: { size: '10mg' }, inStock: true },
    ],
    quantityDiscounts: [],
    category: 'peptides',
    tags: ['research', 'laboratory'],
    metadata: {
      purity: '99.5%',
      testMethod: 'HPLC',
      storageConditions: 'Store at -20°C. Protect from light.',
      handlingInstructions: 'Handle with standard PPE. For laboratory research use only.',
      safetyInfo: 'Follow SDS guidelines.',
      sequence: 'Ac-Nle-cyclo[Asp-His-D-Phe-Arg-Trp-Lys]-OH',
      format: 'Lyophilized White Powder',
      batchNumber: '4945',
      testDate: 'Jan 12, 2024',
      coaUrl: '#coa-pt-141',
    },
    createdAt: '2023-06-01T10:00:00Z',
    updatedAt: '2024-01-12T10:00:00Z',
  },
  {
    id: 'melanotan-2',
    name: 'Melanotan II',
    slug: 'melanotan-2',
    description: 'Melanotan II peptide for melanogenesis research. Synthetic analog of alpha-MSH.',
    shortDescription: 'Alpha-MSH analog for laboratory research.',
    price: 38.00,
    sku: 'MT2-10MG',
    trackInventory: true,
    inventoryQuantity: 180,
    status: 'active',
    images: [
      { id: 'img-7', url: mockR2ImageRef('img-7'), altText: 'Melanotan II 10mg Lyophilized Powder Vial', position: 0 },
    ],
    variants: [
      { id: 'mt2-10mg', productId: 'melanotan-2', name: '10mg', sku: 'MT2-10MG', price: 38.00, inventoryQuantity: 180, options: { size: '10mg' }, inStock: true },
    ],
    quantityDiscounts: [],
    category: 'peptides',
    tags: ['melanocortin', 'research', 'laboratory', 'alpha-msh'],
    metadata: {
      purity: '99.3%',
      testMethod: 'HPLC',
      storageConditions: 'Store at -20°C. Protect from light.',
      handlingInstructions: 'Handle with standard PPE. For laboratory research use only.',
      safetyInfo: 'Follow SDS guidelines.',
      sequence: 'Ac-Nle-cyclo[Asp-His-D-Phe-Arg-Trp-Lys]-NH2',
      format: 'Lyophilized White Powder',
      batchNumber: '4950',
      testDate: 'Jan 15, 2024',
      coaUrl: '#coa-melanotan-2',
    },
    createdAt: '2023-07-01T10:00:00Z',
    updatedAt: '2024-01-18T08:00:00Z',
  },
  {
    id: 'semax',
    name: 'Semax',
    slug: 'semax',
    description: 'Synthetic peptide derived from ACTH for laboratory research purposes.',
    shortDescription: 'ACTH-derived peptide for research.',
    price: 65.00,
    sku: 'SEMAX-30MG',
    trackInventory: true,
    inventoryQuantity: 45,
    status: 'active',
    images: [
      { id: 'img-8', url: mockR2ImageRef('img-8'), altText: 'Semax 30mg Lyophilized Powder Vial', position: 0 },
    ],
    variants: [
      { id: 'semax-30mg', productId: 'semax', name: '30mg', sku: 'SEMAX-30MG', price: 65.00, inventoryQuantity: 45, options: { size: '30mg' }, inStock: true },
    ],
    quantityDiscounts: [],
    category: 'peptides',
    tags: ['acth-analog', 'research', 'laboratory', 'nootropic-peptide'],
    metadata: {
      purity: '99.6%',
      testMethod: 'HPLC',
      storageConditions: 'Store at -20°C. Protect from light.',
      handlingInstructions: 'Handle with standard PPE. For laboratory research use only.',
      safetyInfo: 'Follow SDS guidelines.',
      sequence: 'Met-Glu-His-Phe-Pro-Gly-Pro',
      format: 'Lyophilized White Powder',
      batchNumber: '4955',
      testDate: 'Jan 18, 2024',
      coaUrl: '#coa-semax',
    },
    createdAt: '2023-08-01T10:00:00Z',
    updatedAt: '2024-01-14T16:00:00Z',
  },
];

/**
 * Mock orders data
 */
export const mockOrders: Order[] = [
  {
    id: 'ORD-001',
    storeId: 'golden-peptides',
    customerId: 'cust-001',
    status: 'delivered',
    items: [
      { id: 'item-1', orderId: 'ORD-001', productId: 'bpc-157', name: 'BPC-157 (Acetate)', sku: 'BPC-157-5MG', quantity: 2, unitPrice: 63.00, totalPrice: 126.00 },
    ],
    subtotal: 126.00,
    tax: 10.40,
    shipping: 0,
    discount: 0,
    total: 136.40,
    currency: 'USD',
    shippingAddress: {
      firstName: 'John',
      lastName: 'Researcher',
      address1: '123 Lab Street',
      city: 'Miami',
      state: 'FL',
      zip: '33101',
      country: 'US',
    },
    shippingMethod: 'Priority Mail',
    trackingNumber: '9400111899223456789012',
    createdAt: '2024-01-15T14:30:00Z',
    updatedAt: '2024-01-18T10:00:00Z',
  },
  {
    id: 'ORD-002',
    storeId: 'golden-peptides',
    customerId: 'cust-002',
    status: 'shipped',
    items: [
      { id: 'item-2', orderId: 'ORD-002', productId: 'tb-500', name: 'TB-500 (Thymosin Beta-4)', sku: 'TB500-5MG', quantity: 1, unitPrice: 55.00, totalPrice: 55.00 },
      { id: 'item-3', orderId: 'ORD-002', productId: 'ghk-cu', name: 'GHK-Cu (Copper Peptide)', sku: 'GHKCU-50MG', quantity: 3, unitPrice: 45.00, totalPrice: 135.00 },
    ],
    subtotal: 190.00,
    tax: 15.68,
    shipping: 9.99,
    discount: 0,
    total: 215.67,
    currency: 'USD',
    shippingAddress: {
      firstName: 'Sarah',
      lastName: 'Scientist',
      address1: '456 Research Ave',
      city: 'Boston',
      state: 'MA',
      zip: '02101',
      country: 'US',
    },
    shippingMethod: 'Standard',
    trackingNumber: '9400111899223456789013',
    createdAt: '2024-01-16T09:15:00Z',
    updatedAt: '2024-01-17T16:00:00Z',
  },
  {
    id: 'ORD-003',
    storeId: 'golden-peptides',
    customerId: 'cust-003',
    status: 'paid',
    items: [
      { id: 'item-4', orderId: 'ORD-003', productId: 'ipamorelin', name: 'Ipamorelin 5mg', sku: 'IPA-5MG', quantity: 5, unitPrice: 42.00, totalPrice: 210.00 },
    ],
    subtotal: 210.00,
    tax: 17.33,
    shipping: 0,
    discount: 21.00,
    total: 206.33,
    currency: 'USD',
    shippingAddress: {
      firstName: 'Michael',
      lastName: 'Lab',
      address1: '789 Science Blvd',
      city: 'San Diego',
      state: 'CA',
      zip: '92101',
      country: 'US',
    },
    createdAt: '2024-01-18T11:00:00Z',
    updatedAt: '2024-01-18T11:30:00Z',
  },
  {
    id: 'ORD-004',
    storeId: 'golden-peptides',
    status: 'pending',
    items: [
      { id: 'item-5', orderId: 'ORD-004', productId: 'semax', name: 'Semax', sku: 'SEMAX-30MG', quantity: 2, unitPrice: 65.00, totalPrice: 130.00 },
    ],
    subtotal: 130.00,
    tax: 10.73,
    shipping: 9.99,
    discount: 0,
    total: 150.72,
    currency: 'USD',
    createdAt: '2024-01-19T08:45:00Z',
    updatedAt: '2024-01-19T08:45:00Z',
  },
];

/**
 * Mock customers data
 */
export const mockCustomers: Customer[] = [
  {
    id: 'cust-001',
    storeId: 'golden-peptides',
    email: 'john.researcher@lab.edu',
    firstName: 'John',
    lastName: 'Researcher',
    phone: '+1 305-555-0101',
    defaultAddress: {
      address1: '123 Lab Street',
      city: 'Miami',
      state: 'FL',
      zip: '33101',
      country: 'US',
    },
    totalOrders: 5,
    totalSpent: 682.00,
    createdAt: '2023-06-15T10:00:00Z',
  },
  {
    id: 'cust-002',
    storeId: 'golden-peptides',
    email: 'sarah.scientist@university.edu',
    firstName: 'Sarah',
    lastName: 'Scientist',
    phone: '+1 617-555-0202',
    defaultAddress: {
      address1: '456 Research Ave',
      city: 'Boston',
      state: 'MA',
      zip: '02101',
      country: 'US',
    },
    totalOrders: 12,
    totalSpent: 1890.50,
    createdAt: '2023-03-20T10:00:00Z',
  },
  {
    id: 'cust-003',
    storeId: 'golden-peptides',
    email: 'michael.lab@research.org',
    firstName: 'Michael',
    lastName: 'Lab',
    totalOrders: 3,
    totalSpent: 425.00,
    createdAt: '2023-11-01T10:00:00Z',
  },
];

/**
 * Mock coupons data
 */
export const mockCoupons: Coupon[] = [
  {
    id: 'coup-001',
    storeId: 'golden-peptides',
    code: 'RESEARCH10',
    description: '10% off for research institutions',
    discountType: 'percentage',
    discountValue: 10,
    minOrderValue: 100,
    maxUses: 100,
    usesCount: 23,
    active: true,
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'coup-002',
    storeId: 'golden-peptides',
    code: 'FREESHIP',
    description: 'Free shipping on all orders',
    discountType: 'free_shipping',
    discountValue: 0,
    usesCount: 45,
    active: true,
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'coup-003',
    storeId: 'golden-peptides',
    code: 'BULK20',
    description: '$20 off orders over $250',
    discountType: 'fixed',
    discountValue: 20,
    minOrderValue: 250,
    maxUses: 50,
    usesCount: 12,
    active: true,
    createdAt: '2024-01-10T00:00:00Z',
  },
];

/**
 * Mock store
 */
export const mockStore: Store = {
  id: 'store-1',
  name: 'The Golden Peptides',
  agencyId: 'agency-1',
  siteId: 'site-1',
  settings: {
    currency: 'USD',
    taxRate: 7.5,
    shippingZones: [
      {
        id: 'zone-1',
        name: 'United States',
        countries: ['US'],
        rates: [
          { id: 'rate-1', name: 'Standard Shipping', price: 5.99, estimatedDays: '5-7 days' },
          { id: 'rate-2', name: 'Express Shipping', price: 12.99, estimatedDays: '2-3 days' },
          { id: 'rate-3', name: 'Free Shipping', price: 0, minOrderValue: 75, estimatedDays: '5-7 days' },
        ],
      },
      {
        id: 'zone-2',
        name: 'International',
        countries: ['CA', 'GB', 'AU', 'DE', 'FR'],
        rates: [
          { id: 'rate-4', name: 'Standard International', price: 19.99, estimatedDays: '10-14 days' },
        ],
      },
    ],
    paymentMethods: ['stripe', 'nmi'],
  },
  createdAt: '2024-01-01T00:00:00Z',
  updatedAt: '2024-01-15T00:00:00Z',
};

/**
 * Mock store statistics
 */
export const mockStoreStats: StoreStats = {
  totalOrders: 156,
  totalRevenue: 24850.75,
  averageOrderValue: 159.30,
  totalCustomers: 89,
  totalProducts: 8,
  lowStockProducts: 2,
  recentOrders: mockOrders.slice(0, 5),
};

/**
 * Get product by ID
 */
export function getProductById(id: string): Product | undefined {
  return mockProducts.find(p => p.id === id);
}

/**
 * Get product by slug
 */
export function getProductBySlug(slug: string): Product | undefined {
  return mockProducts.find(p => p.slug === slug);
}

/**
 * Search products
 */
export function searchProducts(query: string, category?: string): Product[] {
  return filterProductsBySearch(mockProducts, query, category);
}

/**
 * Get products by category
 */
export function getProductsByCategory(category: string): Product[] {
  return mockProducts.filter(p => p.category === category);
}

/**
 * Get featured products
 */
export function getFeaturedProducts(limit: number = 4): Product[] {
  return mockProducts
    .filter(p => p.tags?.includes('bestseller') || p.inventoryQuantity > 100)
    .slice(0, limit);
}

/**
 * Get related products
 */
export function getRelatedProducts(productId: string, limit: number = 4): Product[] {
  const product = getProductById(productId);
  if (!product) return [];

  return mockProducts
    .filter(p => p.id !== productId && p.category === product.category)
    .slice(0, limit);
}
